import {
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS,
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS_COUNT,
  useManagementArchiveUnitTaskGroup,
  useManagementCancelUnitTaskGroup,
  useManagementDeleteUnitTaskGroup
} from '@apps-common/apis/management/unit-task/task-groups'
import {
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE,
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE_COUNT
} from '@apps-common/apis/management/unit-task/task-queue'
import { ProgramTaskModifyModal } from '@apps-common/apps/portal/pages/units/pages/program-tasks/modals'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import {
  UNIT_TASK_STATUS_ARCHIVEABLE_STATUSES,
  UNIT_TASK_STATUS_CANCELLABLE_STATUSES,
  UNIT_TASK_STATUS_DELETABLE_STATUSES,
  UNIT_TASK_STATUS_EDITABLE_STATUSES,
  UNIT_TASK_STATUS_RETRYABLE_STATUSES
} from '@apps-common/config/unit-task'
import { IAppNavigation } from '@apps-common/hooks/useNavigation'
import { queryClient } from '@apps-common/lib'
import { IUnitTaskGroupModel } from '@apps-common/types/portal/unit-tasks'
import { ROUTE_UNIT_PROGRAM_TASK_QUEUE } from '@apps-radionet-portal-root/src/config/private-routes'

interface IProps {
  task: IUnitTaskGroupModel
  navigation: IAppNavigation
}

export const TasksOptions: React.FC<IProps> = ({ task, navigation }) => {
  const { navigateForward } = navigation

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS() })
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS_COUNT() })
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE() })
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE_COUNT() })
  }

  const { mutate: archiveTask, isPending: archiveIsPending } = useManagementArchiveUnitTaskGroup(true, () => invalidateQueries())
  const { mutate: deleteTask, isPending: deleteIsPending } = useManagementDeleteUnitTaskGroup(true, () => invalidateQueries())
  const { mutate: cancelTask, isPending: cancelIsPending } = useManagementCancelUnitTaskGroup(true, () => invalidateQueries())

  const disabled = archiveIsPending || deleteIsPending || cancelIsPending

  return (
    <div className='flex items-center justify-end gap-[8px]'>
      {UNIT_TASK_STATUS_RETRYABLE_STATUSES.includes(task.status) && (
        <ModalButton
          component={ProgramTaskModifyModal}
          disabled={disabled}
          classes=''
          componentProps={{
            taskId: task.id,
            title: 'Retry task',
            editAction: 'retry'
          }}
          tooltip={{ id: `program-task-retry-${task?.id}`, message: 'Retry' }}
        >
          <ButtonRoundSm type='div' className='action-success'>
            <commonIcons.reload />
          </ButtonRoundSm>
        </ModalButton>
      )}
      {UNIT_TASK_STATUS_EDITABLE_STATUSES.includes(task.status) && (
        <ModalButton
          component={ProgramTaskModifyModal}
          disabled={disabled}
          classes=''
          componentProps={{
            taskId: task.id,
            title: 'Update task',
            editAction: 'update'
          }}
          tooltip={{ id: `program-task-update-${task?.id}`, message: 'Update' }}
        >
          <ButtonRoundSm type='div' className='action-neutral'>
            <commonIcons.edit />
          </ButtonRoundSm>
        </ModalButton>
      )}
      {UNIT_TASK_STATUS_ARCHIVEABLE_STATUSES.includes(task.status) && (
        <ModalButton
          component={ConfirmModal}
          disabled={disabled}
          classes=''
          componentProps={{
            text: 'Archive',
            description: 'Confirm you would like to archive this task?',
            action: ConfirmModalAction.Neutral
          }}
          confirmCallback={() => archiveTask({ model: task })}
          tooltip={{ id: `program-task-archive-${task?.id}`, message: 'Archive' }}
        >
          <ButtonRoundSm type='div' className='action-neutral'>
            <commonIcons.archive />
          </ButtonRoundSm>
        </ModalButton>
      )}
      {UNIT_TASK_STATUS_DELETABLE_STATUSES.includes(task.status) && (
        <ModalButton
          component={ConfirmModal}
          disabled={disabled}
          classes=''
          componentProps={{
            text: 'Delete',
            description: 'Confirm you would like to delete this task?',
            action: ConfirmModalAction.Error
          }}
          confirmCallback={() => deleteTask({ model: task })}
          tooltip={{ id: `program-task-delete-${task?.id}`, message: 'Delete' }}
        >
          <ButtonRoundSm type='div' className='action-error'>
            <commonIcons.delete />
          </ButtonRoundSm>
        </ModalButton>
      )}
      {UNIT_TASK_STATUS_CANCELLABLE_STATUSES.includes(task.status) && (
        <ModalButton
          component={ConfirmModal}
          disabled={disabled}
          classes=''
          componentProps={{
            text: 'Cancel',
            description: 'Confirm you would like to cancel this task?',
            action: ConfirmModalAction.Warning
          }}
          confirmCallback={() => cancelTask({ model: task })}
          tooltip={{ id: `program-task-cancel-${task?.id}`, message: 'Cancel' }}
        >
          <ButtonRoundSm type='div' className='action-warning'>
            <commonIcons.cancel />
          </ButtonRoundSm>
        </ModalButton>
      )}
      <div className='grow inline-block md:hidden'>
        <ButtonRoundSm
          type='button'
          className='action-brand ring-1 grow flex items-center justify-between gap-[5px] jost-light text-[0.8rem] !pl-[15px] !p-[5px] !pr-[2px] h-[30px] w-full rounded-full'
          disabled={disabled}
          callback={() => {
            navigateForward({ routeUrl: ROUTE_UNIT_PROGRAM_TASK_QUEUE.buildPath(task.id), route: ROUTE_UNIT_PROGRAM_TASK_QUEUE })
          }}
          tooltip={{ id: `program-task-queue-${task?.id}`, message: 'Queue' }}
        >
          Queue
          <div className='bg-brand text-on-brand w-[26px] h-[26px] rounded-[13px] flex items-center justify-center'>
            <commonIcons.arrowAngled className='text-[0.9rem]' />
          </div>{' '}
        </ButtonRoundSm>
      </div>
      <ButtonRoundSm
        type='button'
        className='action-brand-solid hidden md:flex'
        disabled={disabled}
        callback={() => {
          navigateForward({ routeUrl: ROUTE_UNIT_PROGRAM_TASK_QUEUE.buildPath(task.id), route: ROUTE_UNIT_PROGRAM_TASK_QUEUE })
        }}
        tooltip={{ id: `program-task-queue-${task?.id}`, message: 'Queue' }}
      >
        <commonIcons.arrowAngled />
      </ButtonRoundSm>
    </div>
  )
}
